import type { CSSProperties } from 'react';
import type { ItemsPerRow, TileSize } from './types';
import { TILE_SIZE_MIN_WIDTH, TILE_SIZE_MAX_WIDTH } from './types';

const DEFAULT_GAP_PX = 16; // mirrors Tailwind gap-4

/**
 * Build inline styles for an "auto-fill but cap at N columns" tile grid.
 *
 * Behaviour:
 * - **Wide containers**: the grid's `maxWidth` is capped at
 *   `itemsPerRow × TILE_SIZE_MAX_WIDTH[tileSize] + gaps`, so tiles render
 *   at their natural max size rather than stretching to fill the screen.
 *   This makes the difference between small/medium/large visually obvious.
 * - **Mid-width containers**: tracks expand from min to fair-share via
 *   `1fr`, settling at exactly `itemsPerRow` columns.
 * - **Narrow containers**: when fair-share would drop below
 *   `TILE_SIZE_MIN_WIDTH[tileSize]`, the minimum wins and `auto-fill`
 *   reduces the column count instead of squishing each tile.
 */
export function tileGridStyle(
  tileSize: TileSize,
  itemsPerRow: ItemsPerRow,
  gapPx: number = DEFAULT_GAP_PX,
): CSSProperties {
  const min = TILE_SIZE_MIN_WIDTH[tileSize];
  const max = TILE_SIZE_MAX_WIDTH[tileSize];
  const totalGap = gapPx * Math.max(0, itemsPerRow - 1);
  // Cap the per-tile floor at half the container (minus one gap) so the grid
  // never collapses to a single column on narrow/mobile widths: when the tile
  // min (e.g. 180px for medium) is wider than half the screen, two-up still
  // renders instead of one. On wider containers half-width exceeds the tile
  // min, so this is a no-op and the configured itemsPerRow is preserved.
  const floor = `min(${min}px, calc((100% - ${gapPx}px) / 2))`;
  return {
    display: 'grid',
    gridTemplateColumns:
      `repeat(auto-fill, minmax(max(${floor}, calc((100% - ${totalGap}px) / ${itemsPerRow})), 1fr))`,
    gap: `${gapPx}px`,
    maxWidth: `${max * itemsPerRow + totalGap}px`,
  };
}
